import { useState } from 'react';
import type { QuotaEventDTO, QuotaPeriodDTO } from '@shared/schemas/quota';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { type UsageColumn, UsageDataTable } from '@/features/usage/components/UsageDataTable';
import { formatQuotaDate, formatSignedTokenDelta, formatTokenCount } from '@/features/quota/quota-format';
import { getQuotaEventLabel, getQuotaPeriodClosedLabel } from '@/features/quota/quota-event-labels';
import {
  HISTORY_PAGE_SIZE,
  useAdminQuotaEvents,
  useAdminQuotaPeriods,
} from '@/features/quota/useAdminUserQuota';

type AdminUserQuotaHistoryTabsProps = {
  userId: string;
};

export function AdminUserQuotaHistoryTabs({ userId }: AdminUserQuotaHistoryTabsProps) {
  const [eventsPage, setEventsPage] = useState(1);
  const [periodsPage, setPeriodsPage] = useState(1);

  const eventsQuery = useAdminQuotaEvents(userId, eventsPage);
  const periodsQuery = useAdminQuotaPeriods(userId, periodsPage);

  const eventColumns: UsageColumn<QuotaEventDTO>[] = [
    {
      key: 'createdAt',
      header: 'Date',
      cell: (row) => <span className="whitespace-nowrap text-muted-foreground">{formatQuotaDate(row.createdAt)}</span>,
    },
    {
      key: 'eventType',
      header: 'Événement',
      cell: (row) => (
        <div className="space-y-1">
          <span className="font-medium text-foreground">{getQuotaEventLabel(row.eventType)}</span>
          {row.reason ? <p className="max-w-[20rem] truncate text-xs text-muted-foreground">{row.reason}</p> : null}
        </div>
      ),
    },
    {
      key: 'actor',
      header: 'Acteur',
      cell: (row) => <span>{row.actorName ?? 'Système'}</span>,
    },
    {
      key: 'tokensDelta',
      header: 'Delta tokens',
      align: 'right',
      cell: (row) => <span className="tabular-nums">{formatSignedTokenDelta(row.tokensDelta)}</span>,
    },
  ];

  const periodColumns: UsageColumn<QuotaPeriodDTO>[] = [
    {
      key: 'period',
      header: 'Période',
      cell: (row) => (
        <span className="whitespace-nowrap text-foreground">
          {formatQuotaDate(row.periodStartAt)} → {formatQuotaDate(row.periodEndAt)}
        </span>
      ),
    },
    {
      key: 'closedReason',
      header: 'Clôture',
      cell: (row) => (
        <Badge variant="secondary">{getQuotaPeriodClosedLabel(row.closedReason)}</Badge>
      ),
    },
    {
      key: 'limit',
      header: 'Limite',
      align: 'right',
      cell: (row) => <span className="tabular-nums">{formatTokenCount(row.tokenLimit + row.extraTokens)}</span>,
    },
    {
      key: 'consumed',
      header: 'Consommé',
      align: 'right',
      cell: (row) => <span className="tabular-nums">{formatTokenCount(row.tokensConsumed)}</span>,
    },
  ];

  return (
    <Tabs defaultValue="events" className="space-y-4">
      <TabsList>
        <TabsTrigger value="events">Événements</TabsTrigger>
        <TabsTrigger value="periods">Périodes</TabsTrigger>
      </TabsList>

      <TabsContent value="events">
        <UsageDataTable
          columns={eventColumns}
          rows={eventsQuery.data?.items ?? []}
          getRowKey={(row) => row.id}
          isLoading={eventsQuery.isLoading}
          emptyLabel="Aucun événement quota enregistré."
          title="Historique des événements"
          description="Journal des actions quota et des bascules de configuration."
          page={eventsPage}
          pageCount={eventsQuery.data?.meta.pageCount ?? 1}
          total={eventsQuery.data?.meta.total}
          pageSize={HISTORY_PAGE_SIZE}
          onPageChange={setEventsPage}
        />
      </TabsContent>

      <TabsContent value="periods">
        <UsageDataTable
          columns={periodColumns}
          rows={periodsQuery.data?.items ?? []}
          getRowKey={(row) => row.id}
          isLoading={periodsQuery.isLoading}
          emptyLabel="Aucune période quota clôturée."
          title="Historique des périodes"
          description="Périodes consommées et renouvellements passés."
          page={periodsPage}
          pageCount={periodsQuery.data?.meta.pageCount ?? 1}
          total={periodsQuery.data?.meta.total}
          pageSize={HISTORY_PAGE_SIZE}
          onPageChange={setPeriodsPage}
        />
      </TabsContent>
    </Tabs>
  );
}
